import type { RuleProblem, RuleValidation } from '@kkp-fraud/shared';
import { hasKey, t } from './i18n';

/**
 * Wave 2-0: the console does not check rules itself any more. The Rust engine is the one
 * checker (`POST /api/rules/validate`), and it answers every message twice — as the English
 * sentence it has always sent (`errors[i]`) and as data (`problems[i]`: `path`, `code`,
 * `params`). This module turns that answer into what the screens show: the words come from
 * the catalogue (`problem.<code>`, en + th), the place from `path`. A code this build does not
 * know is shown in the engine's own English sentence — never dropped, never re-derived here.
 *
 * Nothing in this file knows what makes a rule valid. It knows where a message is (paths),
 * how to number it for a person (0-based → 1-based) and which form field a rule-level message
 * is about — presentation, not rules.
 */

export interface CheckedProblem {
  /** Document path without the rule id — `ruleBased[0].branches[1].clauses[0]`; '' = the whole document. */
  path: string;
  /** The engine's template code; '' when it sent none (an older engine, a schema refusal). */
  code: string;
  params: Record<string, string | number>;
  /** The engine's English sentence, shown when the catalogue has no `problem.<code>`. */
  text: string;
}

/** One answer from the engine, and the version of the document it is about. */
export interface CheckResult {
  /** `contentHash` of the document that was checked. */
  hash: string;
  ok: boolean;
  problems: CheckedProblem[];
  warnings: CheckedProblem[];
}

/**
 * Warnings the engine still reports but the console does not show. `unconfirmed_thresholds`
 * is the threshold sign-off, which is no longer part of the console (product owner, 30 Sep).
 */
export const HIDDEN_WARNINGS: ReadonlySet<string> = new Set(['unconfirmed_thresholds']);

/**
 * A short fingerprint of a document's content: two 32-bit FNV-style hashes and the length of
 * its JSON. Only used to tell "the answer is about THIS draft" from "about an earlier one", so
 * two different drafts sharing a fingerprint would at worst show an answer one edit late — and
 * the save path is re-checked by the server anyway.
 */
export function contentHash(doc: unknown): string {
  const s = JSON.stringify(doc) ?? '';
  let a = 0x811c9dc5;
  let b = 0x9747b28c ^ s.length;
  for (let i = 0; i < s.length; i++) {
    const c = s.charCodeAt(i);
    a = Math.imul(a ^ c, 0x01000193);
    b = Math.imul(b ^ c, 0x5bd1e995);
    b ^= b >>> 15;
  }
  return `${(a >>> 0).toString(16)}${(b >>> 0).toString(16)}:${s.length}`;
}

function pair(texts: readonly string[], data: readonly RuleProblem[] | undefined): CheckedProblem[] {
  return texts.map((text, i) => {
    const p = data?.[i];
    return p ? { path: p.path, code: p.code, params: p.params ?? {}, text } : { path: '', code: '', params: {}, text };
  });
}

/** The engine's `validate` answer (200) as a result for the document with hash `hash`. */
export function fromValidation(hash: string, v: RuleValidation): CheckResult {
  return {
    hash,
    ok: v.ok,
    problems: pair(v.errors ?? [], v.problems),
    warnings: pair(v.warnings ?? [], v.warningProblems).filter((w) => !HIDDEN_WARNINGS.has(w.code)),
  };
}

/**
 * The engine's schema refusal (400 `validation_failed`): a document whose top-level shape it
 * will not read at all (`ruleSet.thresholds must be object`). A real answer — "no" — not a
 * failure to answer. Its fields are rebased from `ruleSet.x` to `x`.
 */
export function fromSchemaRefusal(hash: string, problems: readonly { field: string; message: string }[]): CheckResult {
  return {
    hash,
    ok: false,
    problems: problems.map((p) => ({
      path: p.field.replace(/^ruleSet(\.|$)/, ''),
      code: '',
      params: {},
      text: `${p.field}: ${p.message}`,
    })),
    warnings: [],
  };
}

/** Codes of the row conflicts (E1–E3): marked on the action row itself in the editor. */
export const ROW_CONFLICT_CODES: ReadonlySet<string> = new Set(['duplicate_row', 'shadowed_row', 'repeated_condition']);

/** The action row a path is in (`…branches[2]…` → 2), or null. */
export function rowOf(path: string): number | null {
  const m = path.match(/(?:^|\.)branches\[(\d+)\]/);
  return m ? Number(m[1]) : null;
}

/** The value types the engine names (`number`, `string`, `boolean`), worded in the active language. */
const KIND_PARAMS = ['kind', 'actual', 'valueType'] as const;

/** A list in a path (`clauses[2]`) → the catalogue key that names one item of it ("condition 3"). */
const PLACE_ITEM: Readonly<Record<string, string>> = {
  branches: 'place.branch',
  clauses: 'place.clause',
  conditions: 'place.clause',
  measures: 'place.measure',
  filters: 'place.filter',
  setRules: 'place.setRule',
};

/**
 * Where a problem is, in words: `branches[0].measures[1].windowSeconds` → "Action 1 · Counter 2 ·
 * Window", `setRules[0]` → "Set rule 1", `ruleBased[3].clauses[0]` → "RB-04 · Condition 1" (the
 * rule by its id, from the problem's `ruleId`). Items are counted from 1, as the editor labels
 * them; a segment the catalogue has no word for is shown as the engine sent it, never dropped.
 */
export function placeText(path: string, params: Readonly<Record<string, string | number>> = {}): string {
  if (!path) return '';
  const words: string[] = [];
  for (const segment of path.split('.')) {
    const item = segment.match(/^(\w+)\[(\d+)\]$/);
    if (item) {
      const [, list = '', index = '0'] = item;
      const n = Number(index) + 1;
      if (list === 'ruleBased' || list === 'patternBased') {
        words.push(typeof params.ruleId === 'string' && params.ruleId !== '' ? params.ruleId : t('place.rule', { n }));
      } else {
        const key = PLACE_ITEM[list];
        words.push(key ? t(key, { n }) : segment);
      }
      continue;
    }
    words.push(hasKey(`place.field.${segment}`) ? t(`place.field.${segment}`) : segment);
  }
  return words.join(' · ');
}

/**
 * The parameters as a person reads them: rows and items counted from 1 (the editor labels them
 * "Action 2", "condition 3"), value types and list names in the active language.
 *   n     — the action row the path is in (a rule without rows is its own "Action 1");
 *   at    — the item the path ends at (`clauses[2]` → 3);
 *   other / first — the row / item the message compares with.
 */
function displayParams(p: CheckedProblem): Record<string, string | number> {
  const out: Record<string, string | number> = { ...p.params };
  out.n = (rowOf(p.path) ?? 0) + 1;
  const last = p.path.match(/\[(\d+)\]$/);
  if (last) out.at = Number(last[1]) + 1;
  if (typeof out.other === 'number') out.other += 1;
  if (typeof out.first === 'number') out.first += 1;
  for (const k of KIND_PARAMS) {
    const v = out[k];
    if (typeof v === 'string' && hasKey(`fieldkind.${v}.label`)) out[k] = t(`fieldkind.${v}.label`);
  }
  if (typeof out.list === 'string' && hasKey(`problem.item.${out.list}`)) out.list = t(`problem.item.${out.list}`);
  // An operator as the editor labels it (`in_set` → "in Master list"); an unknown one stays as sent.
  if (typeof out.op === 'string' && hasKey(`op.${out.op}.label`)) out.op = t(`op.${out.op}.label`);
  // 2a expressions — `unit_mismatch` sends its verb and units in English (`add`, `count`,
  // `field:amount_thb`); word them in the active language.
  if (typeof out.verb === 'string') {
    const k = out.verb.replace(/ /g, '_');
    if (hasKey(`expr.verb.${k}`)) out.verb = t(`expr.verb.${k}`);
  }
  for (const side of ['left', 'right'] as const) {
    const u = out[side];
    if (typeof u !== 'string') continue;
    if (u.startsWith('field:')) out[side] = t('expr.unit.field', { field: u.slice('field:'.length) });
    else if (hasKey(`expr.unit.${u}`)) out[side] = t(`expr.unit.${u}`);
  }
  return out;
}

/** The problem in the active language — or the engine's English sentence for a code this build does not know. */
/** A field history does not keep → the history field that carries the same thing, if one does. */
const HISTORY_STAND_IN: Readonly<Record<string, string>> = { amount: 'amount_thb', event_time: 'event_epoch_ms' };

export function problemText(p: CheckedProblem): string {
  if (p.code === 'filter_field_not_history' || p.code === 'aggregate_field_not_history') {
    const use = HISTORY_STAND_IN[String(p.params?.field ?? '')];
    if (use) return t('problem.not_history.use', { field: String(p.params?.field), use });
  }
  const key = `problem.${p.code}`;
  return p.code && hasKey(key) ? t(key, displayParams(p)) : p.text;
}

/**
 * The form field a message about the rule itself is about, so the editor can mark that input.
 * A message whose path already names its place (a clause, a row) is not in this table.
 */
const FIELD_OF: Readonly<Record<string, string>> = {
  missing_id: 'id',
  duplicate_rule_id: 'id',
  id_type: 'id',
  id_format: 'id',
  missing_name: 'name',
  name_type: 'name',
  priority_type: 'priority',
  unknown_action: 'action',
  points_invalid: 'points',
  enabled_type: 'enabled',
  threshold_confirmed_type: 'thresholdConfirmed',
  requires_not_strings: 'requires',
  clauses_required: 'clauses',
  branches_not_array: 'branches',
  rb_mirror: 'branches',
  pb_mirror: 'branches',
  measures_required: 'measures',
  unknown_scope: 'scope',
  unknown_event: 'event',
  unknown_aggregate: 'aggregate',
  threshold_invalid: 'threshold',
  window_invalid: 'windowSeconds',
  window_too_long: 'windowSeconds',
  // Wave 7 long windows — a window over 1 day in whole days only; some aggregates not on it
  window_whole_days: 'windowSeconds',
  window_long_aggregate: 'aggregate',
  aggregate_needs_field: 'aggregateField',
  unknown_aggregate_field: 'aggregateField',
  sum_needs_number: 'aggregateField',
  aggregate_field_not_history: 'aggregateField',
  filters_not_array: 'filters',
  // 2c set rules — the set rule's own inputs
  duplicate_set_rule_id: 'id',
  min_hits_invalid: 'minHits',
  min_hits_too_high: 'minHits',
  count_actions_invalid: 'countActions',
  of_not_array: 'of',
};

/** `path` extended to the field the message is about: `branches[1].measures[0]` + `scope`. */
export function fieldPath(p: CheckedProblem): string {
  const named = p.code === 'not_a_string' || p.code === 'row_not_a_number' ? p.params.field : undefined;
  const field = typeof named === 'string' ? named : FIELD_OF[p.code];
  if (!field) return p.path;
  return p.path ? `${p.path}.${field}` : field;
}

/**
 * The problems about one rule — `family[index]` in the checked document — with paths relative
 * to the rule ('' = the rule itself). A duplicate id is reported on whichever rule the engine
 * met second; when that is another rule carrying this rule's id, it is this rule's problem too.
 */
export function problemsOfRule(
  problems: readonly CheckedProblem[],
  family: 'ruleBased' | 'patternBased',
  index: number,
  ruleId: string,
): CheckedProblem[] {
  const prefix = `${family}[${index}]`;
  const out: CheckedProblem[] = [];
  for (const p of problems) {
    if (p.path === prefix || p.path.startsWith(`${prefix}.`)) {
      out.push({ ...p, path: p.path.slice(prefix.length).replace(/^\./, '') });
    } else if (p.code === 'duplicate_rule_id' && ruleId !== '' && p.params.id === ruleId) {
      out.push({ ...p, path: '' });
    }
  }
  return out;
}

/**
 * The engine checks a rule with action rows twice: its rule-level fields (which mirror row 1)
 * and every row. The editor shows row 1, so a rule-level message that repeats one of row 1's —
 * same code, same parameters — is dropped: `clauses[0]` beside `branches[0].clauses[0]`, and a
 * pattern rule's `scope` beside `branches[0].measures[0]`'s.
 */
export function withoutMirrorRepeats(problems: readonly CheckedProblem[]): CheckedProblem[] {
  const key = (p: CheckedProblem, path: string) => `${p.code}|${path}|${JSON.stringify(p.params)}`;
  const row1 = new Set(
    problems
      .filter((p) => /^branches\[0\](\.|$)/.test(p.path))
      .map((p) => key(p, p.path.replace(/^branches\[0\]\.measures\[0\]\.?/, '').replace(/^branches\[0\]\.?/, ''))),
  );
  return problems.filter((p) => rowOf(p.path) !== null || !row1.has(key(p, p.path)));
}

/**
 * One problem as a single line with its place: `ruleBased[0].clauses[1]: …` in the active
 * language, or the engine's own sentence (which already starts with its place) for a code
 * this build does not know.
 */
export function problemLine(p: CheckedProblem): string {
  const key = `problem.${p.code}`;
  if (!(p.code && hasKey(key))) return p.text;
  return p.path ? `${p.path}: ${problemText(p)}` : problemText(p);
}

/** A refusal's `validation` as one line — the dry run's error banner. */
export function validationLine(v: RuleValidation): string {
  return fromValidation('', v).problems.map(problemLine).join(' · ');
}

/**
 * The problems of one condition chain by row: `prefix` is the chain's path (`clauses`,
 * `branches[1].clauses`, `branches[0].measures[1].filters`), the map is keyed by the 0-based
 * condition index.
 */
export function chainProblems(problems: readonly CheckedProblem[], prefix: string): Map<number, CheckedProblem[]> {
  const out = new Map<number, CheckedProblem[]>();
  const escaped = prefix.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const re = new RegExp(`^${escaped}\\[(\\d+)\\]$`);
  for (const p of problems) {
    const m = p.path.match(re);
    if (!m) continue;
    const i = Number(m[1]);
    out.set(i, [...(out.get(i) ?? []), p]);
  }
  return out;
}
